// Display summary for a contact's PocketFlows sync error, shared by the
// ContactsSheet and CreateEditContactDrawer error cards.
//
// Why derive from `pocketflow_sync_error_fields` and not the message: the
// free-text `pocketflow_sync_error` only carries the LAST rejection PF
// returned (e.g. "retry failed: Invalid phone number" after the retry that
// dropped a bad email), so a contact rejected on BOTH email and phone would
// read as phone-only. The categorized fields list persists every rejected
// category across both sync passes, so it is the complete signal.
//
// Wording deliberately avoids "marketing"/"sync"/provider language — the
// admin just needs to know the contact's details are invalid and what the
// contact can't receive because of it.

export type ContactSyncErrorSummary = {
  title: string;
  detail: string;
};

export function contactSyncErrorSummary(contact: {
  pocketflow_sync_error?: unknown;
  pocketflow_sync_error_fields?: unknown;
}): ContactSyncErrorSummary | null {
  const message = contact.pocketflow_sync_error;
  // Presence of the message IS the error signal (REMOVEd atomically on the
  // next successful sync) — same gate the cards used before this helper.
  if (typeof message !== 'string' || message.length === 0) return null;

  const rawFields = contact.pocketflow_sync_error_fields;
  const fields = Array.isArray(rawFields) ? rawFields : [];
  const hasEmail = fields.includes('email');
  const hasPhone = fields.includes('phone');

  if (hasEmail && hasPhone) {
    return {
      title: 'Invalid email address & phone number',
      detail:
        "This contact's email address and phone number are invalid — they can't receive emails or text messages until they're fixed.",
    };
  }
  if (hasEmail) {
    return {
      title: 'Invalid email address',
      detail:
        "This contact's email address is invalid — they can't receive emails until it's fixed.",
    };
  }
  if (hasPhone) {
    return {
      title: 'Invalid phone number',
      detail:
        "This contact's phone number is invalid — they can't receive text messages until it's fixed.",
    };
  }
  // Uncategorized (legacy row not yet re-synced, or an `other`-category
  // rejection): fall back to the raw message under a generic title.
  return { title: 'Contact error', detail: message };
}
